// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/AgentSteps.test.tsx
import {
  type AskPunttAgentStep,
  type AskPunttCheckProgress,
} from "@mg/schemas/src/christo/askPuntt";
import {
  CaretDown,
  CaretRight,
  CheckCircle,
  CircleNotch,
  XCircle,
} from "@phosphor-icons/react";
import { Text } from "@radix-ui/themes";
import cx from "classnames";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  STATUS_PANEL_ITEMS_MUTED_CLASS,
  STATUS_PANEL_TOGGLE_HOVER_CLASS,
  STATUS_PANEL_TOGGLE_MUTED_CLASS,
} from "./statusPanelTokens";
// why: the salvage parser lives in a leaf module now (review #5399) -- it is
// pure string/JSON logic, this file was already past the 400-line ceiling, and
// the cut-point cases are only falsifiable when they can be called directly.
import {
  capabilityInstructions,
  fanOutReports,
  parseTruncatedJson,
  stringField,
} from "./truncatedJson";

import { interactiveSurfaceClasses } from "../../../components/InteractiveSurface";

type StepPair = {
  call: AskPunttAgentStep & { type: "tool_call" };
  result?: AskPunttAgentStep & { type: "tool_result" };
};

type SubagentRequest = {
  name: string;
  task: string;
  /**
   * Slot in the RAW `args.requests` array, kept because warhol's
   * `run_subagents` returns "one report per request, in order" over that raw
   * list (`zip(requests, outcomes, strict=True)` in `agents/spawn.py`) -- even
   * for a request this panel drops, which still gets a `failed` report in its
   * slot from `_lookup`. Matching reports by position AFTER the drop shifted
   * every later report up one row, so a surviving subagent showed its
   * neighbour's verdict.
   */
  sourceIndex: number;
};

type SubagentReportView = {
  name?: string;
  status?: string;
  summary?: string;
  error?: string;
  output?: string;
};

type SubagentRequestEntry = {
  request: SubagentRequest;
  index: number;
  report?: SubagentReportView;
};

type SubagentRequestGroup = {
  name: string;
  entries: SubagentRequestEntry[];
};

/**
 * The verdicts christo lifts out of a `run_subagents` return BEFORE capping
 * `summary`. One name, so the rows and the header read the same wire field.
 */
type SubagentOutcomes = Extract<
  AskPunttAgentStep,
  { type: "tool_result" }
>["subagentOutcomes"];

function parseSubagentRequests(
  args: Record<string, unknown> | undefined,
): SubagentRequest[] {
  const requests = args?.requests;
  if (!Array.isArray(requests)) return [];
  return requests
    .map((entry, sourceIndex) => {
      if (entry == null || typeof entry !== "object") return null;
      const row = entry as Record<string, unknown>;
      const name = stringField(row.name);
      const task = stringField(row.task);
      if (!name) return null;
      return { name, task, sourceIndex };
    })
    .filter((row): row is SubagentRequest => row != null);
}

function parseSubagentReports(summary: string): SubagentReportView[] | null {
  const trimmed = summary.trim();
  // why: warhol's budget capability wraps a fan-out's list as
  // `{result: [...], budget: {...}}`, so the reports JSON starts with `{`, not
  // `[`. Requiring `[` dropped every body of a real fan-out -- the rows still
  // appeared, from the outcomes christo lifts out, but each one expanded to an
  // empty box.
  if (!trimmed.startsWith("[") && !trimmed.startsWith("{")) return null;
  const parsed = fanOutReports(parseTruncatedJson(trimmed));
  if (!parsed) return null;
  return parsed.map((entry) =>
    entry != null && typeof entry === "object"
      ? (entry as SubagentReportView)
      : {},
  );
}

function parseSubagentReport(summary: string): SubagentReportView | null {
  const trimmed = summary.trim();
  if (!trimmed.startsWith("{")) return null;
  const parsed = parseTruncatedJson(trimmed);
  return parsed != null && typeof parsed === "object"
    ? (parsed as SubagentReportView)
    : null;
}

function groupConsecutiveSubagentRequests(
  requests: SubagentRequest[],
  reports: SubagentReportView[] | null,
): SubagentRequestGroup[] {
  const groups: SubagentRequestGroup[] = [];
  for (let index = 0; index < requests.length; index += 1) {
    const request = requests[index];
    const last = groups[groups.length - 1];
    const entry: SubagentRequestEntry = {
      request,
      index,
      // By the request's slot in the wire array, not its row here: a dropped
      // request still owns a report slot upstream.
      report: reports?.[request.sourceIndex],
    };
    if (last && last.name === request.name) {
      last.entries.push(entry);
      continue;
    }
    groups.push({ name: request.name, entries: [entry] });
  }
  return groups;
}

function SubagentSameNameGroup({
  stepId,
  groupIndex,
  name,
  entries,
  done,
  checkProgress,
}: {
  stepId: string;
  groupIndex: number;
  name: string;
  entries: SubagentRequestEntry[];
  done: boolean;
  /** What warhol has published for this turn's legs; see `rowTick`. */
  checkProgress?: AskPunttCheckProgress[];
}) {
  const { t } = useTranslation("ui");
  const [expanded, setExpanded] = useState(false);
  const rowId = `${stepId}-${groupIndex}`;
  const label = t("agentSteps.subagentGroupLabel", {
    name,
    count: entries.length,
  });
  // A group is only failed when every member failed; one bad reviewer among
  // good ones belongs on its own row, not on the group's.
  const reportFailed =
    done &&
    entries.length > 0 &&
    entries.every((entry) => subagentReportFailed(entry.report));
  // While the turn runs there is no report yet, so warhol's own report of the
  // legs decides. It cannot say WHICH member of a same-named group settled, so
  // the group is as fine-grained as warhol allows; the member rows below keep
  // the pair's tick.
  const tick = groupTick(checkProgress, name, entries.length, done);
  const failed = tick.failed ?? reportFailed;
  const groupDone = tick.done;

  return (
    <div
      data-testid={`agent-step-subagent-group-${rowId}`}
      className={cx(
        "overflow-hidden",
        interactiveSurfaceClasses({
          expandable: true,
          muted: true,
          compact: true,
          open: expanded,
        }),
      )}
    >
      <button
        type="button"
        data-testid={`agent-step-subagent-group-toggle-${rowId}`}
        aria-expanded={expanded}
        aria-label={label}
        onClick={() => setExpanded((open) => !open)}
        className={`${STATUS_PANEL_TOGGLE_MUTED_CLASS} ${STATUS_PANEL_TOGGLE_HOVER_CLASS}`}
      >
        {failed ? (
          <XCircle
            size={14}
            weight="fill"
            className="text-tomato-900 shrink-0"
            aria-hidden
          />
        ) : groupDone ? (
          <CheckCircle
            size={14}
            weight="fill"
            className="shrink-0 text-malachite-700"
            aria-hidden
          />
        ) : (
          <CircleNotch
            size={14}
            className="shrink-0 animate-spin text-puntt-accent-9 motion-reduce:animate-none"
            aria-hidden
          />
        )}
        <Text
          size="1"
          weight="regular"
          data-testid="agent-step-name"
          className="min-w-0 flex-1 truncate text-puntt-neutral-gray-12"
        >
          {label}
        </Text>
        {expanded ? (
          <CaretDown
            size={14}
            className="shrink-0 text-puntt-neutral-gray-12"
            aria-hidden
          />
        ) : (
          <CaretRight
            size={14}
            className="shrink-0 text-puntt-neutral-gray-12"
            aria-hidden
          />
        )}
      </button>
      {expanded ? (
        <div
          data-testid={`agent-step-subagent-group-body-${rowId}`}
          className={`${STATUS_PANEL_ITEMS_MUTED_CLASS} space-y-0.5`}
        >
          {entries.map((entry) => (
            <SubagentToolStep
              key={`${stepId}-${entry.index}`}
              stepId={stepId}
              requestIndex={entry.index}
              title={entry.request.name}
              body={entry.request.task}
              report={entry.report}
              done={done}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * warhol's `SubagentReport.status` is `Literal["ok", "failed", "timed_out"]`
 * (`agents/_subagents.py`); only `ok` carries an output.
 */
const FAILED_SUBAGENT_STATUSES = new Set(["failed", "timed_out"]);

/**
 * Whether a report says its subagent did NOT do the work.
 *
 * The row used to tick as soon as the fan-out returned, so a reviewer that
 * failed or timed out was drawn exactly like one that finished -- the chip
 * claimed work that never happened. An unknown status is not read as a
 * failure: a vocabulary this does not know is a reason to stay quiet, not to
 * accuse.
 */
function subagentReportFailed(report: SubagentReportView | undefined): boolean {
  if (!report) return false;
  return FAILED_SUBAGENT_STATUSES.has(stringField(report.status).toLowerCase());
}

/**
 * The report rows for a fan-out, from both sources the step carries.
 *
 * The capped summary is the only source with report BODIES, but it is also
 * unreliable in a way that matters here: `parseSubagentReports` salvages
 * truncated JSON, so it can hand back the first few reports of a six-reviewer
 * fan-out and drop the rest -- and the dropped ones are exactly where a failure
 * hides. The outcomes christo lifts out before capping are complete and carry
 * every verdict, so they decide the ROWS, and the parsed reports only fill in
 * the bodies.
 */
function mergeSubagentReports(
  parsed: SubagentReportView[] | null,
  outcomes: SubagentOutcomes,
): SubagentReportView[] | null {
  if (!outcomes?.length) return parsed;
  return outcomes.map((outcome, index) => {
    const body = parsed?.[index];
    return {
      ...body,
      name: outcome.name || body?.name || "",
      // christo keeps a slot it could not read rather than dropping it, so the
      // index pairing above still holds; such a slot carries no verdict, and
      // the body's own status is a better answer than blanking the row.
      status: outcome.status || body?.status,
      ...(outcome.error ? { error: outcome.error } : {}),
    };
  });
}

function reportDetail(report: SubagentReportView | undefined): string {
  if (!report) return "";
  const summary = stringField(report.summary);
  if (summary) return summary;
  const error = stringField(report.error);
  if (error) return error;
  const output = stringField(report.output);
  if (output) return output;
  const status = stringField(report.status);
  return status;
}

/**
 * warhol's two failure statuses (`contracts/progress.py::ProgressStatus`).
 */
const FAILED_CHECK_STATUSES = new Set(["failed", "timed_out"]);

/**
 * The tick for one row, from warhol's own report of the leg behind it.
 *
 * `done = !!pair.result || !isStreaming` ticks every row of a fan-out together
 * when the whole call returns — so a reviewer that timed out was drawn exactly
 * like one that finished. warhol publishes each leg the moment IT settles,
 * which is what lets the rows be told apart while the turn runs.
 *
 * The NEWEST entry for a name decides: a leg the model retried publishes twice
 * and the second one is the truth. A name warhol has published nothing for —
 * an older session, a turn whose progress row could not be read, and every
 * turn that is not warhol's — keeps the old fallback exactly, so nothing that
 * works today changes.
 */
function rowTick(
  checkProgress: readonly AskPunttCheckProgress[] | undefined,
  name: string,
  fallbackDone: boolean,
  rowCount = 1,
): { done: boolean; failed?: boolean } {
  const entries = (checkProgress ?? []).filter((entry) => entry.name === name);
  // No entry: defer to the report, by leaving `failed` ABSENT rather than
  // false. A plain `false` would override `subagentReportFailed(report)` and
  // draw a reviewer that failed as a finished tick — the exact defect the
  // report's own status was added for.
  if (entries.length === 0) return { done: fallbackDone };
  const failedCount = entries.filter((entry) =>
    FAILED_CHECK_STATUSES.has(entry.status),
  ).length;

  // More than one row shares this name and the legs did NOT all end the same
  // way — warhol runs one `evidence-verifier` per intent, in one call. warhol
  // publishes in FINISH order and these rows are drawn in REQUEST order, so
  // which row is which is not knowable here. Taking the newest entry for all
  // of them drew a timed-out verifier as a green tick beside its finished
  // twin; marking them all failed would libel the ones that worked. Neither
  // row can be attributed, so both keep the pair's own tick.
  if (rowCount > 1 && failedCount > 0 && failedCount < entries.length) {
    return { done: fallbackDone };
  }
  const failed = failedCount > 0;
  return { done: !failed, failed };
}

/**
 * The tick for a group of same-named legs.
 *
 * A group's members share one roster name, so no entry can be attributed to a
 * particular member — the group is as fine-grained as warhol's own report
 * allows. Any member that failed marks the group; fewer entries than members
 * means a leg is still running, and a group with work outstanding is not done.
 */
function groupTick(
  checkProgress: readonly AskPunttCheckProgress[] | undefined,
  name: string,
  memberCount: number,
  fallbackDone: boolean,
): { done: boolean; failed?: boolean } {
  const statuses = (checkProgress ?? [])
    .filter((entry) => entry.name === name)
    .slice(-memberCount)
    .map((entry) => entry.status);
  // Absent, not false, when warhol has published nothing; see `rowTick`.
  if (statuses.length === 0) return { done: fallbackDone };
  const failedCount = statuses.filter((status) =>
    FAILED_CHECK_STATUSES.has(status),
  ).length;

  // The SAME rule the report-derived tick uses, so the header does not change
  // its mind when the turn ends and the reports take over from the checks: a
  // group is failed when EVERY member failed, done when every member settled
  // and none did. Anything else — still running, or a mixed group whose
  // members cannot be told apart — keeps the pair's tick rather than
  // guessing, and a group that was red at minute 3 does not turn green at
  // minute 4.
  if (failedCount === statuses.length) return { done: true, failed: true };
  if (failedCount === 0 && statuses.length >= memberCount) {
    return { done: true, failed: false };
  }
  return { done: fallbackDone };
}

function SubagentToolStep({
  stepId,
  requestIndex,
  title,
  body,
  report,
  done,
  failedStatus,
}: {
  stepId: string;
  requestIndex?: number;
  title: string;
  body: string;
  report?: SubagentReportView;
  done: boolean;
  /**
   * warhol says this leg did not do the work. Wins over both `done` and the
   * report: while the turn runs there is no report to read, and once one lands
   * the report's own status already agrees.
   */
  failedStatus?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const suffix = requestIndex != null ? `-${requestIndex}` : "";
  const rowId = `${stepId}${suffix}`;
  const reportText = reportDetail(report);
  const failed = failedStatus ?? subagentReportFailed(report);

  return (
    <div
      data-testid={`agent-step-subagent-${rowId}`}
      className={cx(
        "overflow-hidden",
        interactiveSurfaceClasses({
          expandable: true,
          muted: true,
          compact: true,
          open: expanded,
        }),
      )}
    >
      <button
        type="button"
        data-testid={`agent-step-subagent-toggle-${rowId}`}
        aria-expanded={expanded}
        aria-label={title}
        onClick={() => setExpanded((open) => !open)}
        className={`${STATUS_PANEL_TOGGLE_MUTED_CLASS} ${STATUS_PANEL_TOGGLE_HOVER_CLASS}`}
      >
        {failed ? (
          <XCircle
            size={14}
            weight="fill"
            className="text-tomato-900 shrink-0"
            aria-hidden
          />
        ) : done ? (
          <CheckCircle
            size={14}
            weight="fill"
            className="shrink-0 text-malachite-700"
            aria-hidden
          />
        ) : (
          <CircleNotch
            size={14}
            className="shrink-0 animate-spin text-puntt-accent-9 motion-reduce:animate-none"
            aria-hidden
          />
        )}
        <Text
          size="1"
          weight="regular"
          data-testid="agent-step-name"
          className="min-w-0 flex-1 truncate text-puntt-neutral-gray-12"
        >
          {title}
        </Text>
        {expanded ? (
          <CaretDown
            size={14}
            className="shrink-0 text-puntt-neutral-gray-12"
            aria-hidden
          />
        ) : (
          <CaretRight
            size={14}
            className="shrink-0 text-puntt-neutral-gray-12"
            aria-hidden
          />
        )}
      </button>
      {expanded ? (
        <div
          data-testid={`agent-step-subagent-body-${rowId}`}
          className={STATUS_PANEL_ITEMS_MUTED_CLASS}
        >
          {body ? (
            <Text
              size="1"
              className="whitespace-pre-wrap text-puntt-neutral-gray-12"
            >
              {body}
            </Text>
          ) : null}
          {reportText ? (
            <Text
              size="1"
              className={`whitespace-pre-wrap text-puntt-neutral-gray-9 ${body ? "mt-1" : ""}`}
            >
              {reportText}
            </Text>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * One expandable row per subagent spawn / skill load.
 *
 * `checkProgress` is what warhol has published so far this turn. It is what
 * lets one reviewer's row tick while its neighbours are still running, instead
 * of every row ticking together when the whole fan-out returns.
 */
export function SubagentToolStepsForPair({
  pair,
  isStreaming,
  checkProgress,
}: {
  pair: StepPair;
  isStreaming: boolean;
  checkProgress?: AskPunttCheckProgress[];
}) {
  const call = pair.call;
  const done = !!pair.result || !isStreaming;
  const stepId = call.stepId || call.timestamp;
  const args = call.args ?? {};

  if (call.tool === "run_subagents") {
    const requests = parseSubagentRequests(args);
    const parsedReports = pair.result
      ? parseSubagentReports(pair.result.summary)
      : null;
    const outcomes = pair.result?.subagentOutcomes;
    const reports = mergeSubagentReports(parsedReports, outcomes);
    /**
     * How many rows this fan-out draws for one roster name. A name on more
     * than one row cannot be attributed to a row from warhol's finish-ordered
     * entries; see `rowTick`.
     */
    const reportCount = (name: string) =>
      (reports ?? []).filter((report) => stringField(report.name) === name)
        .length;
    if (requests.length === 0) {
      // why: rembrandt converts a `tool_call` into a `tool_result` in the SAME
      // array slot, and the `tool_result` schema carries no `args` -- so a
      // settled fan-out, and EVERY reloaded thread, reaches this panel with no
      // `requests` at all and the reports JSON alone in `summary`. The bare
      // fallback row passed no report, so each finished fan-out collapsed to
      // one nameless row whose expanded box was empty: every subagent and its
      // verdict was dropped. That is the same wire fact `run_subagent` and
      // `load_capability` already recover from below.
      if (reports && reports.length > 0) {
        return (
          <>
            {reports.map((report, reportIndex) => {
              const name = stringField(report.name);
              const tick = rowTick(
                checkProgress,
                name,
                done,
                reportCount(name),
              );
              return (
                <SubagentToolStep
                  key={`${stepId}-${reportIndex}`}
                  stepId={stepId}
                  requestIndex={reportIndex}
                  title={name || call.displayName || "Run subagents"}
                  body=""
                  report={report}
                  done={tick.done}
                  failedStatus={tick.failed}
                />
              );
            })}
          </>
        );
      }
      return (
        <SubagentToolStep
          stepId={stepId}
          title={call.displayName || "Run subagents"}
          body=""
          done={done}
        />
      );
    }
    const groups = groupConsecutiveSubagentRequests(requests, reports);
    return (
      <>
        {groups.map((group, groupIndex) =>
          group.entries.length === 1 ? (
            (() => {
              const entry = group.entries[0];
              const tick = rowTick(checkProgress, entry.request.name, done);
              return (
                <SubagentToolStep
                  key={`${stepId}-${entry.index}`}
                  stepId={stepId}
                  requestIndex={entry.index}
                  title={entry.request.name}
                  body={entry.request.task}
                  report={entry.report}
                  done={tick.done}
                  failedStatus={tick.failed}
                />
              );
            })()
          ) : (
            <SubagentSameNameGroup
              key={`${stepId}-group-${groupIndex}`}
              stepId={stepId}
              groupIndex={groupIndex}
              name={group.name}
              entries={group.entries}
              done={done}
              checkProgress={checkProgress}
            />
          ),
        )}
      </>
    );
  }

  if (call.tool === "run_subagent") {
    const name = stringField(args.name) || call.displayName || "Run subagent";
    const task = stringField(args.task);
    const report = pair.result
      ? (parseSubagentReport(pair.result.summary) ?? undefined)
      : undefined;
    const tick = rowTick(checkProgress, stringField(args.name), done);
    return (
      <SubagentToolStep
        stepId={stepId}
        title={name}
        body={task}
        report={report}
        done={tick.done}
        failedStatus={tick.failed}
      />
    );
  }

  if (call.tool === "load_capability") {
    const instructions =
      stringField(args.instructions) ||
      capabilityInstructions(pair.result?.summary);
    return (
      <SubagentToolStep
        stepId={stepId}
        title={call.displayName || "Load skill"}
        body={instructions}
        done={done}
      />
    );
  }

  return null;
}

export function countSubagentLogicalSteps(
  tool: string,
  args: Record<string, unknown> | undefined,
  /**
   * The merged `tool_result` summary, when one landed. It is the only place a
   * settled fan-out still lists its subagents — the in-place merge drops
   * `args.requests` — so without it the header counted a finished 3-subagent
   * fan-out as "1 Step" and disagreed with the rows rendered under it.
   */
  resultSummary?: string,
  /**
   * The verdicts christo lifted out BEFORE capping `summary`. Without them the
   * header counted only what the capped JSON still parsed into: a settled
   * fan-out whose summary was cut mid-object salvaged nothing and read
   * "1 Step" above the N reviewer rows `mergeSubagentReports` rendered from
   * these same outcomes.
   */
  outcomes?: SubagentOutcomes,
): number {
  if (tool === "run_subagents") {
    const requests = parseSubagentRequests(args);
    if (requests.length > 0) return requests.length;
    const parsed = resultSummary ? parseSubagentReports(resultSummary) : null;
    // The rows come from `mergeSubagentReports`, so the count does too --
    // anything else re-derives the row set and can disagree with it again.
    const reports = mergeSubagentReports(parsed, outcomes);
    return reports && reports.length > 0 ? reports.length : 1;
  }
  return 1;
}
